import React, { useState } from "react";
import { Star, Check, X, Lock, TrendingDown, Info, ChevronDown } from "lucide-react";
import RegistrarLogo from "./RegistrarLogo";
import { useCurrency } from "../lib/currency";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

function Money({ v }) {
  const { formatPrice } = useCurrency();
  if (v == null) return <span className="text-zinc-300">—</span>;
  return <span className="tabular-nums font-medium text-zinc-900">{formatPrice(v)}</span>;
}

function BundlePill({ row }) {
  if (row.three_year_bundle) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-700" data-testid={`bundle-${row.slug}`}>
        <Lock className="h-2.5 w-2.5" /> Locked rate
      </span>
    );
  }
  const pct = row.bundle_discount_pct;
  if (pct == null || pct < 1) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700" data-testid={`bundle-${row.slug}`}>
      <TrendingDown className="h-2.5 w-2.5" /> Save {pct.toFixed(0)}%
    </span>
  );
}

function WhoisCell({ row }) {
  return (
    <div className="inline-flex items-center gap-1">
      {row.whois_privacy_included ? (
        <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
          <Check className="h-3 w-3" /> WHOIS
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 rounded-md bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-500">
          <X className="h-3 w-3" /> No WHOIS
        </span>
      )}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="What is WHOIS privacy?"
            data-testid={`whois-info-${row.slug}`}
            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 text-sm" side="top" align="end">
          <div className="space-y-2">
            <div className="font-display text-sm font-semibold text-zinc-900">WHOIS privacy</div>
            <p className="text-xs leading-relaxed text-zinc-600">
              WHOIS is a public directory of domain ownership. Without privacy, your name, email, phone and address are searchable by anyone — leading to spam and scam calls.
            </p>
            <div className="border-t border-zinc-100 pt-2">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{row.name}'s policy</div>
              <p className="mt-1 text-xs leading-relaxed text-zinc-700">
                {row.whois_note ||
                  (row.whois_privacy_included
                    ? "WHOIS privacy is included free with every domain."
                    : "WHOIS privacy is not included by default and may be sold separately.")}
              </p>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function RegistrarRow({ row }) {
  const { formatPrice } = useCurrency();
  const [expanded, setExpanded] = useState(false);
  const features = row.features || [];

  return (
    <div
      data-testid={`registrar-row-${row.slug}`}
      className={`group relative rounded-2xl border bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-md ${
        row.best_deal ? "border-indigo-300 ring-2 ring-indigo-100" : "border-zinc-200"
      }`}
    >
      {row.best_deal && (
        <div className="absolute -top-2.5 left-5 rounded-full bg-indigo-600 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
          Best deal
        </div>
      )}

      <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-5">
        {/* Identity (logo, name, badges) */}
        <div className="flex items-start gap-3 md:w-52 md:flex-shrink-0">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white border border-zinc-100">
            <RegistrarLogo registrar={row} size={28} />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <div className="font-display text-base font-semibold text-zinc-900">{row.name}</div>
              {row.live && (
                <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-emerald-700" data-testid={`live-${row.slug}`}>
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
                <Star className="h-3 w-3 fill-amber-400 stroke-amber-400" />
                {row.trust_score.toFixed(1)}
              </span>
              <BundlePill row={row} />
              {row.three_year_total != null && (
                <span
                  className="inline-flex items-center gap-1 rounded-md bg-zinc-50 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-zinc-600"
                  title="Total cost over 3 years (register year 1 + renewal years 2–3)"
                  data-testid={`threeyear-${row.slug}`}
                >
                  3-yr {formatPrice(row.three_year_total)}
                  {row.best_3y && <span className="text-emerald-600 font-semibold">↓</span>}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Prices */}
        <div className="grid flex-1 grid-cols-3 gap-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Register</div>
            <div className="mt-1 text-base"><Money v={row.register} /></div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Renewal</div>
            <div className="mt-1 text-base"><Money v={row.renewal} /></div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Transfer</div>
            <div className="mt-1 text-base"><Money v={row.transfer} /></div>
          </div>
        </div>

        {/* Right cluster: WHOIS + info, then Buy */}
        <div className="flex items-center gap-3 md:w-44 md:flex-shrink-0">
          <WhoisCell row={row} />
          <a
            href={row.buy_url || row.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`buy-${row.slug}`}
            className="ml-auto inline-flex items-center gap-1 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-900 transition-all hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
          >
            Buy here
          </a>
        </div>
      </div>

      {/* Show more — features dropdown */}
      {features.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`show-more-${row.slug}`}
            aria-expanded={expanded}
            className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-zinc-500 transition-colors hover:text-indigo-700"
          >
            {expanded ? "Show less" : "Show more"}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
          {expanded && (
            <div
              data-testid={`features-${row.slug}`}
              className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 border-t border-zinc-100 pt-3 sm:grid-cols-2"
            >
              {features.map((f, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-zinc-700">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                  <span className="leading-snug">{f}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
